import React from 'react';
import { createCustomEqual } from 'fast-equals';
import { isLatLngLiteral } from '@googlemaps/typescript-guards';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const deepCompareEqualsForMaps = createCustomEqual((deepEqual) => (a, b) => {
    if (isLatLngLiteral(a) || a instanceof google.maps.LatLng || isLatLngLiteral(b) || b instanceof google.maps.LatLng) {
        return new google.maps.LatLng(a).equals(new google.maps.LatLng(b));
    }
    // TODO extend to other types
    // use fast-equals for other objects
    return deepEqual(a, b);
});
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function useDeepCompareMemoize(value) {
    const ref = React.useRef();
    if (!deepCompareEqualsForMaps(value, ref.current)) {
        ref.current = value;
    }
    return ref.current;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function useDeepCompareEffectForMaps(callback, dependencies) {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(callback, dependencies.map(useDeepCompareMemoize));
}
const Map = ({ onClick, onIdle, children, style, ...options }) => {
    const ref = React.useRef(null);
    const [map, setMap] = React.useState();
    React.useEffect(() => {
        if (ref.current && !map) {
            // Create map with initial options so mapId is present before markers mount.
            setMap(new window.google.maps.Map(ref.current, options));
        }
    }, [ref, map, options]);
    // because React does not do deep comparisons, a custom hook is used
    // see discussion in https://github.com/googlemaps/js-samples/issues/946
    useDeepCompareEffectForMaps(() => {
        if (map) {
            map.setOptions(options);
        }
    }, [map, options]);
    React.useEffect(() => {
        if (!map) {
            return;
        }
        ['click', 'idle'].forEach((eventName) => google.maps.event.clearListeners(map, eventName));
        if (onClick) {
            map.addListener('click', onClick);
        }
        if (onIdle) {
            map.addListener('idle', () => onIdle(map));
        }
    }, [map, onClick, onIdle]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: ref, style: style }),
        React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
                // set the map prop on the child component
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                return React.cloneElement(child, { map });
            }
            return null;
        })));
};
export default Map;
